import { useState, useEffect } from "react";
import { groupBy, concat } from "lodash";

import {
  BSC_MAINNET,
  MANTLE_MAINNET,
  ARBITRUM,
  MOVING_AVERAGE_PERIOD,
  MOVING_AVERAGE_DAYS,
  S3_URL_BASE,
  PLATFORM_TOKENS,
} from "../constants";

import { SUBGRAPH_CATEGORY_KEY } from "../util/getSubgraphClient";

import {
  useVolumeData,
  useFeesData,
  useUsersData,
  useTradersData,
  useRequest,
  useGraph,
} from "../dataProvider";
import {
  useTotalVolume,
  useTotalFee,
  useTotalUsers,
  useTotalOpenInterest,
  useTotalValueLocked,
} from "./useTotalData";

const getTotalAndDelta = (list) => {
  const notReady = list.find((item) => item.length === 0);

  if (notReady) {
    return [];
  }

  let total = 0;
  let delta = 0;

  list.forEach((item) => {
    total = total + item[0];
    delta = delta + item[1];
  });

  return [total, delta];
};

export function useAllChainTotalVolume() {
  const bscTotalVolumesData = useTotalVolume(BSC_MAINNET);
  const mantleTotalVolumesData = useTotalVolume(MANTLE_MAINNET);
  const arbitrumTotalVolumesData = useTotalVolume(ARBITRUM);

  return getTotalAndDelta([
    bscTotalVolumesData,
    mantleTotalVolumesData,
    arbitrumTotalVolumesData,
  ]);
}

export function useAllChainTotalFees() {
  const bscTotalFee = useTotalFee(BSC_MAINNET);
  const mantleTotalFee = useTotalFee(MANTLE_MAINNET);
  const arbitrumTotalFee = useTotalFee(ARBITRUM);

  return getTotalAndDelta([bscTotalFee, mantleTotalFee, arbitrumTotalFee]);
}

export function useAllChainTotalUsers() {
  const bscTotalUsers = useTotalUsers(BSC_MAINNET);
  const mantleTotalUsers = useTotalUsers(MANTLE_MAINNET);
  const arbitrumTotalUsers = useTotalUsers(ARBITRUM);

  return getTotalAndDelta([
    bscTotalUsers,
    mantleTotalUsers,
    arbitrumTotalUsers,
  ]);
}

export function useAllChainTotalOpenInterest() {
  const bscTotalOpenInterest = useTotalOpenInterest(BSC_MAINNET);
  const mantleTotalOpenInterest = useTotalOpenInterest(MANTLE_MAINNET);
  const arbitrumTotalOpenInterest = useTotalOpenInterest(ARBITRUM);

  return getTotalAndDelta([
    bscTotalOpenInterest,
    mantleTotalOpenInterest,
    arbitrumTotalOpenInterest,
  ]);
}

export function useAllChainTotalTVL() {
  const bscTotalTVL = useTotalValueLocked(BSC_MAINNET);
  const mantleTotalTVL = useTotalValueLocked(MANTLE_MAINNET);
  const arbitrumTotalTVL = useTotalValueLocked(ARBITRUM);

  return getTotalAndDelta([bscTotalTVL, mantleTotalTVL, arbitrumTotalTVL]);
}

const handleVolumeAndFeeData = (
  groupValues,
  keyArray = [BSC_MAINNET, MANTLE_MAINNET, ARBITRUM],
  key = "timestamp"
) => {
  const cumulativeByTs = {};
  const finalArr = groupValues.map((subArr) => {
    const chainInfo = {};
    let all = 0;
    let cumulative = 0;
    let movingAverageAll;

    subArr.forEach((item) => {
      all += item.all;
      cumulative += item.cumulative;
    });

    chainInfo.cumulative = cumulative;
    chainInfo.all = all;
    const timestamp = subArr[0][key];
    chainInfo[key] = timestamp;
    const movingAverageTs = timestamp - MOVING_AVERAGE_PERIOD;
    if (movingAverageTs in cumulativeByTs) {
      movingAverageAll =
        (cumulative - cumulativeByTs[movingAverageTs]) / MOVING_AVERAGE_DAYS;
    }
    cumulativeByTs[timestamp] = cumulative;
    chainInfo.movingAverageAll = movingAverageAll;

    for (const id of keyArray) {
      const item = subArr.find((item) => item.chain === id);
      chainInfo[id] = item?.all ?? 0;
    }

    return chainInfo;
  });

  return finalArr;
};

/**
 *
 * @param {*} dataArr An array of data needs to be merged
 * @param {*} groupDataHandler The function to handle timestamp grouped array
 * @param {*} keyArray the item key in the final array item. The default sequence is [BSC, Mantle]
 * @param {*} key the timestamp key name in the data
 */
const groupDataByTimestamp = (
  dataArr,
  keyArray = [BSC_MAINNET, MANTLE_MAINNET, ARBITRUM],
  key = "timestamp"
) => {
  const parsedDataArr = dataArr.map((data, id) => {
    data.forEach((item) => {
      item.chain = keyArray[id];
    });
    return data;
  });

  const mergedArr = concat(...parsedDataArr);
  const groupObj = groupBy(mergedArr, key);
  const groupValues = Object.values(groupObj);

  return groupValues;
};

export function useAllChainDailyVolume(from, to) {
  const [isLoading, setIsLoading] = useState(true);
  const [res, setRes] = useState(null);

  const [bscVolumeData] = useVolumeData({ from, to, chainName: BSC_MAINNET });
  const [mantleVolumeData] = useVolumeData({
    from,
    to,
    chainName: MANTLE_MAINNET,
  });
  const [arbitrumVolumeData] = useVolumeData({ from, to, chainName: ARBITRUM });

  useEffect(() => {
    // if the chain volume data is not ready
    if (!bscVolumeData || !mantleVolumeData || !arbitrumVolumeData) return;

    const group = groupDataByTimestamp([
      bscVolumeData,
      mantleVolumeData,
      arbitrumVolumeData,
    ]);
    const finalData = handleVolumeAndFeeData(group);
    setRes(finalData);
    setIsLoading(false);
  }, [
    bscVolumeData?.length,
    mantleVolumeData?.length,
    arbitrumVolumeData?.length,
  ]);

  return [res, isLoading];
}

export function useAllChainDailyFee(from, to) {
  const [isLoading, setIsLoading] = useState(true);
  const [res, setRes] = useState(null);

  const [bscFeeData] = useFeesData(
    { from, to, chainName: BSC_MAINNET },
    handleVolumeAndFeeData
  );
  const [mantleFeeData] = useFeesData({
    from,
    to,
    chainName: MANTLE_MAINNET,
  });
  const [arbitrumFeeData] = useFeesData({
    from,
    to,
    chainName: ARBITRUM,
  });

  useEffect(() => {
    // if the chain volume data is not ready
    if (!bscFeeData || !mantleFeeData || !arbitrumFeeData) return;

    const group = groupDataByTimestamp([
      bscFeeData,
      mantleFeeData,
      arbitrumFeeData,
    ]);
    const finalData = handleVolumeAndFeeData(group);
    setRes(finalData);
    setIsLoading(false);
  }, [bscFeeData?.length, mantleFeeData?.length, arbitrumFeeData?.length]);

  return [res, isLoading];
}

const handleUserData = (
  groupValues,
  keyArray = [BSC_MAINNET, MANTLE_MAINNET, ARBITRUM],
  key = "timestamp"
) => {
  const finalArr = groupValues.map((subArr) => {
    const chainInfo = {};
    let all = 0;
    let cumulative = 0;

    subArr.forEach((item) => {
      all += item.uniqueCount;
      cumulative += item.cumulativeNewUserCount;
    });

    chainInfo.cumulative = cumulative;
    chainInfo.all = all;
    const timestamp = subArr[0][key];
    chainInfo[key] = timestamp;

    for (const id of keyArray) {
      const item = subArr.find((item) => item.chain === id);
      chainInfo[id] = item?.uniqueCount ?? 0;
    }

    return chainInfo;
  });

  return finalArr;
};

export function useAllChainDailyUsers(from, to) {
  const [isLoading, setIsLoading] = useState(true);
  const [res, setRes] = useState(null);

  const [bscUserData] = useUsersData({ from, to, chainName: BSC_MAINNET });
  const [mantleUserData] = useUsersData({
    from,
    to,
    chainName: MANTLE_MAINNET,
  });

  const [arbitrumUserData] = useUsersData({
    from,
    to,
    chainName: ARBITRUM,
  });
  useEffect(() => {
    // if the chain volume data is not ready
    if (!bscUserData || !mantleUserData || !arbitrumUserData) return;

    const group = groupDataByTimestamp([
      bscUserData,
      mantleUserData,
      arbitrumUserData,
    ]);
    const finalData = handleUserData(group);
    setRes(finalData);
    setIsLoading(false);
  }, [bscUserData?.length, mantleUserData?.length, arbitrumUserData?.length]);

  return [res, isLoading];
}

const handleTraderData = (
  groupValues,
  keyArray = [BSC_MAINNET, MANTLE_MAINNET, ARBITRUM],
  key = "timestamp"
) => {
  const finalArr = groupValues.map((subArr) => {
    let all = 0;
    const chainInfo = {};
    subArr.forEach((item) => {
      all += item.openInterest;
    });
    chainInfo.all = all;
    chainInfo.timestamp = subArr[0][key];

    for (const id of keyArray) {
      const item = subArr.find((item) => item.chain === id);
      chainInfo[id] = item?.openInterest ?? 0;
    }

    return chainInfo;
  });

  return finalArr;
};

export function useAllChainOpenInterest(from, to) {
  const [isLoading, setIsLoading] = useState(true);
  const [res, setRes] = useState(null);

  const [bscTraderData] = useTradersData({ from, to, chainName: BSC_MAINNET });
  const [mantleTraderData] = useTradersData({
    from,
    to,
    chainName: MANTLE_MAINNET,
  });
  const [arbitrumTraderData] = useTradersData({
    from,
    to,
    chainName: ARBITRUM,
  });

  useEffect(() => {
    // if the chain volume data is not ready
    if (
      !bscTraderData?.data ||
      !mantleTraderData?.data ||
      !arbitrumTraderData?.data
    )
      return;

    const group = groupDataByTimestamp([
      bscTraderData.data,
      mantleTraderData.data,
      arbitrumTraderData.data,
    ]);

    const finalData = handleTraderData(group);
    setRes(finalData);
    setIsLoading(false);
  }, [
    bscTraderData?.data?.length,
    mantleTraderData?.data?.length,
    arbitrumTraderData?.data?.length,
  ]);

  return [res, isLoading];
}

export function useKtcCirculatingSupply() {
  const [data] = useRequest(
    `${S3_URL_BASE}ktc_circulating_supply_coingecko.json`
  );
  return data?.result;
}

export function useStakedKtcSupply() {
  const [data] = useRequest(`${S3_URL_BASE}ktc_staked_all_chain.json`);
  return data?.result;
}

export function useKtcHolders() {
  const [data] = useRequest(`${S3_URL_BASE}token_holders.json`);
  return data?.result;
}

// only use mantle ktc price since it's the highest
export function useKtcPrice(chainId = MANTLE_MAINNET) {
  const { KTC } = PLATFORM_TOKENS[chainId];

  const [ktcPriceData] = useGraph(
    `{
      tokens(where: { id: "${KTC.toLowerCase()}" }) {
        derivedUSD
      }
    }`,
    chainId,
    SUBGRAPH_CATEGORY_KEY.STATS,
    true
  );

  return ktcPriceData?.tokens?.[0]?.derivedUSD;
}
